/**
 * pdfMapCapture.js — Capture html2canvas de la carte Leaflet du PDF (10.5.319, D244)
 *
 * Leaflet dessine les tracés (polyline) dans un <svg> plus grand que la carte,
 * placé par un transform. html2canvas 1.4.1 traite ce transform d'un élément
 * SVG autrement que celui d'un élément HTML, et le résultat dépend du
 * navigateur : en 10.5.316 (D242), la correction du clone alignait Chromium et
 * WebKit, mais Firefox gardait un décalage de translation / scale (8 % de la
 * carte, mesuré au banc Playwright). Le tracé ne passe donc plus par le rendu
 * SVG de html2canvas : dans le clone, chaque <svg> du pane des tracés est
 * remplacé par un <canvas> dessiné ici, depuis la géométrie que Leaflet a posée
 * (attribut `d` des <path>, en coordonnées de calque) et la position du pane de
 * la carte lue sur l'écran. Un <canvas> est un élément HTML, que html2canvas
 * place correctement partout ; il reste dans le pane des tracés, sous les
 * marqueurs, comme à l'écran. La carte de l'écran n'est pas touchée.
 */
import html2canvas from 'html2canvas';

const OVERLAY_SVG = '.leaflet-overlay-pane > svg';

// Position du pane de la carte (translate Leaflet) par rapport au coin de la
// zone de contenu du conteneur : coordonnées de calque + position = coordonnées
// de conteneur. Lue sur les boîtes affichées, quel que soit le navigateur.
export function mapPaneOffset(mapEl) {
  const pane = mapEl.querySelector('.leaflet-map-pane');
  if (!pane) return { x: 0, y: 0 };
  const paneRect = pane.getBoundingClientRect();
  const mapRect = mapEl.getBoundingClientRect();
  return {
    x: paneRect.left - mapRect.left - mapEl.clientLeft,
    y: paneRect.top - mapRect.top - mapEl.clientTop,
  };
}

// Attributs posés par le renderer SVG de Leaflet (_updateStyle) : le PDF
// reprend le style du tracé de l'écran, sans valeur propre.
export function readOverlayPaths(svg) {
  return Array.from(svg.querySelectorAll('path')).map((path) => ({
    d:             path.getAttribute('d'),
    stroke:        path.getAttribute('stroke'),
    strokeOpacity: path.getAttribute('stroke-opacity'),
    strokeWidth:   path.getAttribute('stroke-width'),
    lineCap:       path.getAttribute('stroke-linecap'),
    lineJoin:      path.getAttribute('stroke-linejoin'),
    dashArray:     path.getAttribute('stroke-dasharray'),
    dashOffset:    path.getAttribute('stroke-dashoffset'),
    fill:          path.getAttribute('fill'),
    fillOpacity:   path.getAttribute('fill-opacity'),
    fillRule:      path.getAttribute('fill-rule'),
  })).filter((path) => path.d);
}

const toNumber = (value, fallback) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) ? n : fallback;
};

export function parseDashArray(value) {
  if (!value) return [];
  const parts = String(value).split(/[\s,]+/).filter(Boolean).map(Number);
  return parts.some((n) => !Number.isFinite(n) || n < 0) ? [] : parts;
}

// Coordonnées de calque → pixels du canvas : (calque + position du pane) × scale.
// Épaisseur et pointillés suivent l'échelle, comme le SVG agrandi.
export function drawOverlayPaths(ctx, paths, offset, scale) {
  ctx.setTransform(scale, 0, 0, scale, offset.x * scale, offset.y * scale);
  paths.forEach((p) => {
    const shape = new Path2D(p.d);
    if (p.fill && p.fill !== 'none') {
      ctx.globalAlpha = toNumber(p.fillOpacity, 1);
      ctx.fillStyle = p.fill;
      ctx.fill(shape, p.fillRule === 'nonzero' ? 'nonzero' : 'evenodd');
    }
    if (p.stroke && p.stroke !== 'none') {
      ctx.globalAlpha = toNumber(p.strokeOpacity, 1);
      ctx.strokeStyle = p.stroke;
      ctx.lineWidth = toNumber(p.strokeWidth, 1);
      ctx.lineCap = p.lineCap || 'butt';
      ctx.lineJoin = p.lineJoin || 'miter';
      ctx.setLineDash(parseDashArray(p.dashArray));
      ctx.lineDashOffset = toNumber(p.dashOffset, 0);
      ctx.stroke(shape);
    }
  });
  ctx.globalAlpha = 1;
}

// Canvas du clone, de la taille de la zone de contenu de la carte, placé dans
// le pane des tracés à l'opposé de la position du pane : il recouvre la carte.
export function buildOverlayCanvas(doc, { paths, offset, width, height, scale }) {
  const canvas = doc.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  canvas.style.position = 'absolute';
  canvas.style.left = `${-offset.x}px`;
  canvas.style.top = `${-offset.y}px`;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  drawOverlayPaths(canvas.getContext('2d'), paths, offset, scale);
  return canvas;
}

// Remplace, dans le clone, chaque <svg> du pane des tracés par son canvas
// (même ordre que sur l'écran).
export function replaceOverlaySvgs(clonedDoc, overlays) {
  clonedDoc.querySelectorAll(OVERLAY_SVG).forEach((svg, i) => {
    if (!overlays[i]) return;
    svg.replaceWith(buildOverlayCanvas(clonedDoc, overlays[i]));
  });
}

export function captureMapCanvas(mapEl, options) {
  const scale = toNumber(options.scale, 1);
  const offset = mapPaneOffset(mapEl);
  const overlays = Array.from(mapEl.querySelectorAll(OVERLAY_SVG)).map((svg) => ({
    paths: readOverlayPaths(svg),
    offset,
    width: mapEl.clientWidth,
    height: mapEl.clientHeight,
    scale,
  }));
  return html2canvas(mapEl, { ...options, onclone: (clonedDoc) => replaceOverlaySvgs(clonedDoc, overlays) });
}
